import {
  Building2Icon,
  CalendarDaysIcon,
  ClipboardListIcon,
  Loading2,
  Mail,
  NetworkIcon,
  Pencil,
  PersonIcon,
  PhoneIcon,
  Plus,
} from 'assets/icons';
import Badge from 'components/badge/badge';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import RecordActivity from 'components/record/activity/RecordActivity';
import { sourcesActivity } from 'components/record/activity/activityItems';
import ContactRow, { AmbassadorBadge } from 'components/record/ContactRow';
import ExchangeList from 'components/record/ExchangeList';
import KpiTiles from 'components/record/KpiTiles';
import QuickActions from 'components/record/QuickActions';
import RecordIdentity, { RecordIcon } from 'components/record/RecordIdentity';
import RecordLayout, { RecordSection, RecordTabs } from 'components/record/RecordLayout';
import RecordMissions from 'components/record/RecordMissions';
import { isoDay } from 'helper/timeLabels';
import useCrmContext from 'hooks/useCrmContext';
import Usefetchdata from 'hooks/usefetchdata';
import useRecordChanged from 'hooks/useRecordChanged';
import useRecordPanels from 'hooks/useRecordPanels';
import useTaskPanels from 'hooks/useTaskPanels';
import useTasksChanged from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import crmApi from 'server/crm/crmApi';
import { dateLabel, departmentLabel } from './companiesItems';
import { STAGE_BADGES, exchangeWhen, exchangesOf, fullName, kpiTiles } from './recordItems';

const TABS = ['overview', 'activity', 'missions', 'contacts'];

/**
 * The page of a group, on the model of a company's: its own contacts (the HR
 * of a group works for none of its companies), its activity, and its
 * companies, whose numbers it adds up and whose activity it gathers. The
 * group and its companies are those of Jobbiz (big accounts); its contacts
 * and the sales side come from server/crm until the api has them.
 */
const GroupPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { text } = useText('record');
  const { text: activityText } = useText('recordActivity');
  const { call, ready } = useCrmContext();
  const { userData } = Usefetchdata();
  const { openRecordForm } = useRecordPanels();
  const { openTask, openTaskForm } = useTaskPanels();
  const canSeeInvoices = Boolean(userData?.accessInvoice);
  const [record, setRecord] = useState(null);
  // undefined while they are added up, null when there are none to show.
  const [invoices, setInvoices] = useState(undefined);
  const [failed, setFailed] = useState(null);
  const [tab, setTab] = useState('overview');

  const load = useCallback(
    () =>
      call(crmApi.getGroupRecord, { groupId: id }).then(
        (next) => {
          setRecord(next);
          setFailed(null);
        },
        (error) => setFailed(error.message),
      ),
    [call, id],
  );
  useEffect(() => {
    if (ready) load();
  }, [load, ready]);
  // The invoices of all its companies, added up once the page is shown.
  useEffect(() => {
    if (!ready || !canSeeInvoices) return;
    setInvoices(undefined);
    call(crmApi.getGroupInvoiceTotals, { groupId: id }).then((totals) => setInvoices(totals ?? null), () => setInvoices(null));
  }, [call, id, ready, canSeeInvoices]);
  useRecordChanged(load);
  useTasksChanged(load);

  const team = useMemo(() => record?.team ?? [], [record]);
  const nameOf = useCallback(
    (personId) => {
      const person = team.find((member) => member.id === personId);
      return person ? fullName(person) : text('someone');
    },
    [team, text],
  );
  // A source per record: the group alone, then each of its companies.
  const sources = useMemo(
    () =>
      record
        ? [
            { id: record.group.id, label: record.group.name, option: text('groupAlone'), record: record.history },
            ...record.companies.map((company) => ({ id: company.id, label: company.name, companyId: company.id, record: company.history })),
          ]
        : [],
    [record, text],
  );
  const items = useMemo(
    () => sourcesActivity(sources, { now: new Date(), text: activityText, nameOf }),
    [sources, activityText, nameOf],
  );

  if (failed || !record) {
    return (
      <RecordLayout side={null}>
        {failed ? (
          <CompactEmptyState
            glyph="searchX"
            title={text('groupMissing')}
            description={failed}
            action={{ label: text('backCompanies'), onClick: () => navigate('/company') }}
          />
        ) : (
          <div className="flex justify-center p-8" aria-busy="true">
            <span className="animate-spin">
              <Loading2 wh={24} color="#FF9600" />
            </span>
          </div>
        )}
      </RecordLayout>
    );
  }

  const { group, companies, contacts } = record;
  const now = new Date();
  const clients = companies.filter((company) => company.crm.stage === 'client').length;
  const people = contacts.map((contact) => ({ id: contact.id, name: fullName(contact), email: contact.email, phone: contact.phone }));
  const openForm = (kind, extra = {}) => openRecordForm(kind, { groupId: group.id, recordName: group.name, contacts: people, ...extra });
  const add = (kind) => {
    if (kind === 'task') openTaskForm({ defaults: { type: 'todo' } });
    else openForm(kind === 'all' ? 'note' : kind);
  };
  const openItem = (ref) => {
    if (ref.kind === 'task') openTask(ref.id);
    if (ref.kind === 'client' && ref.companyId) navigate(`/company/${ref.companyId}?onglet=${ref.tab}`);
  };
  const lastExchange = (companyId) =>
    items.filter((item) => item.source === companyId && !item.future && item.kind !== 'client').reduce((latest, item) => (!latest || item.at > latest ? item.at : latest), null);
  const { upcoming, recent } = exchangesOf(items, 4);
  const exchange = (item) => ({
    key: item.key,
    title: item.title,
    detail: [item.from, item.body].filter(Boolean).join(' · '),
    when: exchangeWhen(item),
    onOpen: item.ref ? () => openItem(item.ref) : undefined,
  });
  const contactDetail = (person) => [person.position, person.phone, person.email].filter(Boolean).join(' · ');

  return (
    <RecordLayout
      side={
        <>
          <RecordIdentity
            back={{ label: text('backCompanies'), onClick: () => navigate('/company') }}
            picture={<RecordIcon icon={NetworkIcon} />}
            title={<span className="uppercase">{group.name}</span>}
            badge={<Badge type="INFO">{text('group')}</Badge>}
            subtitle={[
              text(companies.length > 1 ? 'companiesMany' : 'companiesOne', { count: companies.length }),
              clients > 0 && text(clients > 1 ? 'clientsMany' : 'clientsOne', { count: clients }),
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            <QuickActions
              actions={[
                { key: 'note', label: text('actionNote'), icon: Pencil, onClick: () => openForm('note') },
                { key: 'email', label: text('actionEmail'), icon: Mail, onClick: () => openForm('email') },
                { key: 'call', label: text('actionCall'), icon: PhoneIcon, onClick: () => openForm('call') },
                { key: 'task', label: text('actionTask'), icon: ClipboardListIcon, onClick: () => add('task') },
                { key: 'event', label: text('actionMeeting'), icon: CalendarDaysIcon, onClick: () => openForm('event') },
                { key: 'contact', label: text('actionContact'), icon: PersonIcon, onClick: () => openForm('groupContact') },
              ]}
            />
          </RecordIdentity>
          <RecordSection
            title={<span className="ft-lg font-semibold">{text('groupContactsTitle', { count: contacts.length })}</span>}
            action={
              <Button color="SECONDARY" size="SM" onClick={() => openForm('groupContact')}>
                {text('add')}
              </Button>
            }
          >
            {!contacts.length && <p className="text-sm text-gray-600">{text('groupContactsEmpty')}</p>}
            {contacts.slice(0, 3).map((person) => (
              <button
                key={person.id}
                type="button"
                onClick={() => navigate(`/company/group/${group.id}/contacts/${person.id}`)}
                className="-mx-2 block w-[calc(100%+16px)] rounded-lg border-b border-gray-100 px-2 py-2.5 text-left transition-colors last:border-0 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm text-gray-900">{fullName(person)}</span>
                  {person.leadStatus === 'ambassadeur' && <AmbassadorBadge />}
                </span>
                <span className="block truncate text-xs text-gray-700">{person.position || text('noPosition')}</span>
              </button>
            ))}
            {contacts.length > 0 && (
              <Button color="SECONDARY" size="SM" className="mt-3 w-full" onClick={() => setTab('contacts')}>
                {text('allContacts')}
              </Button>
            )}
          </RecordSection>
        </>
      }
    >
      <KpiTiles
        tiles={kpiTiles(
          {
            ...record.kpis,
            invoices: canSeeInvoices ? invoices ?? null : null,
            invoicesLoading: canSeeInvoices && invoices === undefined,
            stage: clients ? 'client' : 'prospect',
          },
          text,
        )}
      />
      <RecordTabs tabs={TABS.map((one) => ({ id: one, label: text(`tab_${one}`) }))} activeTab={tab} onChange={setTab} />
      {tab === 'overview' && (
        <div className="flex flex-col gap-4 pb-6">
          <RecordSection
            title={text('groupCompanies', { count: companies.length })}
            action={
              <Button color="SECONDARY" size="SM" onClick={() => openForm('groupCompany')}>
                <Plus wh={18} color="currentColor" />
                <span>{text('addCompany')}</span>
              </Button>
            }
          >
            {!companies.length && (
              <CompactEmptyState compact glyph="searchX" title={text('groupCompaniesEmptyTitle')} description={text('groupCompaniesEmptyText')} />
            )}
            {companies.map((company) => {
              const last = lastExchange(company.id);
              const missionCount = company.missions.missions.length;
              return (
                <button
                  key={company.id}
                  type="button"
                  onClick={() => navigate(`/company/${company.id}`)}
                  className="-mx-2 grid w-[calc(100%+16px)] grid-cols-[minmax(0,1fr)_88px_110px_130px] items-center gap-3 rounded-lg border-b border-gray-100 px-2 py-2.5 text-left transition-colors last:border-0 hover:bg-gray-50"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-100 text-gray-900">
                      <Building2Icon wh={18} color="currentColor" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium uppercase text-gray-900">{company.name}</span>
                      <span className="block truncate text-xs text-gray-600">{departmentLabel(company.department) || text('noDepartment')}</span>
                    </span>
                  </span>
                  <span>
                    <Badge type={STAGE_BADGES[company.crm.stage]}>{text(`stage_${company.crm.stage}`)}</Badge>
                  </span>
                  <span className="text-right text-sm text-gray-900">
                    {text(missionCount > 1 ? 'missionsMany' : 'missionsOne', { count: missionCount })}
                  </span>
                  <span className="text-right text-xs text-gray-600">
                    {last ? text('lastExchange', { day: dateLabel(isoDay(last), now) }) : text('noExchange')}
                  </span>
                </button>
              );
            })}
          </RecordSection>
          <div className="grid grid-cols-1 gap-4 tab:grid-cols-2">
            <ExchangeList
              title={text('upcoming')}
              items={upcoming.map(exchange)}
              empty={{ glyph: 'calendarX', title: text('upcomingEmptyTitle'), description: text('groupUpcomingEmptyText') }}
            />
            <ExchangeList
              title={text('recent')}
              items={recent.map(exchange)}
              empty={{ glyph: 'inbox', title: text('recentEmptyTitle'), description: text('groupRecentEmptyText') }}
            />
          </div>
        </div>
      )}
      {tab === 'activity' && (
        <RecordActivity
          items={items}
          sources={sources.map((source) => ({ id: source.id, label: source.option ?? source.label }))}
          allLabel={text('groupAndCompanies')}
          onAdd={add}
          onOpen={openItem}
        />
      )}
      {tab === 'missions' && (
        <RecordMissions
          missions={companies.flatMap((company) => company.missions.missions)}
          tempWorkers={companies.flatMap((company) => company.missions.tempWorkers)}
        />
      )}
      {tab === 'contacts' && (
        <div className="flex flex-col gap-4 pb-6">
          <RecordSection
            title={text('groupContactsTitle', { count: contacts.length })}
            action={
              <Button color="SECONDARY" size="SM" onClick={() => openForm('groupContact')}>
                <Plus wh={18} color="currentColor" />
                <span>{text('addContact')}</span>
              </Button>
            }
          >
            {!contacts.length && (
              <CompactEmptyState compact glyph="inbox" title={text('groupContactsEmptyTitle')} description={text('groupContactsEmptyText')} />
            )}
            {contacts.map((person) => (
              <ContactRow
                key={person.id}
                person={person}
                detail={contactDetail(person)}
                onOpen={() => navigate(`/company/group/${group.id}/contacts/${person.id}`)}
                actions={
                  <IconTooltip label={text('editContact', { name: fullName(person) })}>
                    <button
                      type="button"
                      aria-label={text('editContact', { name: fullName(person) })}
                      onClick={() => openForm('groupContact', { contact: person })}
                      className={cardIconButtonClass}
                    >
                      <Pencil wh={18} color="#374151" />
                    </button>
                  </IconTooltip>
                }
              />
            ))}
          </RecordSection>
          {companies.map((company) => (
            <RecordSection key={company.id}>
              <button
                type="button"
                onClick={() => navigate(`/company/${company.id}`)}
                className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-900 underline-offset-2 hover:underline"
              >
                <Building2Icon wh={16} color="currentColor" />
                {company.name}
                <span className="font-normal text-gray-600">
                  · {text(company.members.length > 1 ? 'contactsMany' : 'contactsOne', { count: company.members.length })}
                </span>
              </button>
              {!company.members.length && <p className="text-sm text-gray-600">{text('companyNoContact')}</p>}
              {company.members.map((person) => (
                <ContactRow
                  key={person.id}
                  person={person}
                  size={32}
                  detail={person.position}
                  onOpen={() => navigate(`/company/${company.id}/contacts/${person.id}`)}
                />
              ))}
            </RecordSection>
          ))}
        </div>
      )}
    </RecordLayout>
  );
};

export default GroupPage;
